import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Cloud, Contact, Loader2, MonitorSmartphone, Ticket } from 'lucide-react';
import { usePersonOrbit, type PersonOrbit } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { M365UserCard } from '@/components/cipp/M365UserCard';
import { Field, Panel, Pill, TicketList } from '@/components/insights/RecordKit';
import { fmtAge } from '@/lib/rmm-helpers';
import { ROUTES, backupSaasCustomerPath, cippTenantPath, companyPath, insightsDevicePath } from '@/lib/routes';

/**
 * Person Orbit (`/insights/people/:key`) — one person across Autotask (contacts, open tickets),
 * Microsoft 365 (account, MFA, mailbox from CIPP's report cache; plus the live M365 card when a
 * contact's company has a mapped tenant), SaaS backup seat, and their devices.
 */
export default function PersonOrbitPage() {
  const key = useParams<{ key: string }>().key;
  const { data: p, isLoading, error, isFetching } = usePersonOrbit(key);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
      <Link to={ROUTES.insights.find} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Find
      </Link>
      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Gathering this person from every module…</div>
      ) : error ? (
        <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
      ) : p ? (
        <>
          <PageHeader title={p.name} isFetching={isFetching}
            subtitle={<>
              {p.emails.join(' · ') || 'No email'}
              {p.companies.map((c) => <span key={c.id}> · <Link to={companyPath(c.id)} className="hover:underline">{c.name}</Link></span>)}
            </>} />

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div className="space-y-4">
              <M365Panel p={p} />
              <Panel title={`Open tickets${p.tickets.length ? ` · ${p.tickets.length}` : ''}`} icon={Ticket}>
                {p.contacts.length === 0 ? <Empty>Not an Autotask contact, so no tickets to show.</Empty>
                  : <TicketList items={p.tickets} error={p.ticketsError} empty="No open tickets for this contact." />}
              </Panel>
              <Panel title={`Devices${p.devices.length ? ` · ${p.devices.length}` : ''}`} icon={MonitorSmartphone}>
                {p.devices.length === 0 ? <Empty>No devices tied to this person (Intune primary user, Datto RMM Azure user or last user).</Empty> : (
                  <ul className="divide-y divide-divider">
                    {p.devices.map((d) => (
                      <li key={d.key} className="flex items-center justify-between gap-3 px-4 py-2">
                        <div className="min-w-0">
                          <Link to={insightsDevicePath(d.key)} className="text-sm font-medium text-foreground hover:underline">{d.hostname}</Link>
                          <div className="truncate text-[11px] text-muted-foreground">{[d.os, d.via].filter(Boolean).join(' · ')}</div>
                        </div>
                        {d.online != null ? (d.online ? <Pill tone="good">online</Pill> : <Pill tone="warn">offline {fmtAge(d.lastSeen)}</Pill>)
                          : d.lastSeen && <span className="text-xs text-muted-foreground">synced {fmtAge(d.lastSeen)} ago</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <div className="space-y-4">
              <Panel title="Autotask contacts" icon={Contact}>
                {p.contacts.length === 0 ? <Empty>No Autotask contact has this email.</Empty> : (
                  <ul className="divide-y divide-divider">
                    {p.contacts.map((c) => (
                      <li key={c.id} className="px-4 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{c.name}</span>
                          {!c.active && <Pill tone="muted">inactive</Pill>}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          <Link to={companyPath(c.companyId)} className="hover:underline">{c.companyName}</Link>
                          {c.title && ` · ${c.title}`}
                        </div>
                        {(c.phone || c.mobile) && <div className="text-[11px] text-muted-foreground">{[c.phone, c.mobile].filter(Boolean).join(' · ')}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
              {/* Live card: licences, last sign-ins, out of office (CIPP, a few seconds). */}
              {p.cardContactId != null && <M365UserCard contactId={p.cardContactId} />}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function M365Panel({ p }: { p: PersonOrbit }) {
  if (p.m365.length === 0) {
    return <Panel title="Microsoft 365" icon={Cloud} muted><Empty>No Microsoft 365 user with this UPN in any CIPP tenant.</Empty></Panel>;
  }
  return (
    <>
      {p.m365.map((u) => (
        <Panel key={u.tenant.customerId + u.id} title={`Microsoft 365 · ${u.tenant.displayName}`} icon={Cloud}
          actions={<Link to={cippTenantPath(u.tenant.customerId)} className="text-muted-foreground hover:text-foreground hover:underline">Tenant users →</Link>}>
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="py-1 md:border-r md:border-divider">
              <Field label="Account">
                <span className="flex flex-wrap justify-end gap-1">
                  {u.enabled === false ? <Pill tone="warn">sign-in blocked</Pill> : <Pill tone="good">enabled</Pill>}
                  {u.licensed === false && <Pill tone="muted">unlicensed</Pill>}
                  {u.admin && <Pill tone="warn">admin</Pill>}
                  {u.guest && <Pill tone="muted">guest</Pill>}
                </span>
              </Field>
              <Field label="UPN">{u.upn}</Field>
              <Field label="MFA">
                {!u.mfa ? null : u.mfa.registered == null ? <span className="text-muted-foreground">unknown · no P1</span>
                  : u.mfa.registered ? <Pill tone="good">registered</Pill> : <Pill tone="bad">not registered</Pill>}
              </Field>
              <Field label="Enforced by">{u.mfa ? u.mfa.enforcement ?? <Pill tone="bad">nothing</Pill> : null}</Field>
              {u.mfa && u.mfa.methods.length > 0 && <Field label="Methods">{u.mfa.methods.length}</Field>}
            </div>
            <div className="py-1">
              <Field label="Mailbox">{u.mailbox ? `${u.mailbox.sizeGb ?? 0} GB${u.mailbox.quotaGb ? ` of ${u.mailbox.quotaGb} GB` : ''}` : null}</Field>
              <Field label="Forwarding">{u.mailbox?.forwarding ? <Pill tone="warn">{u.mailbox.forwarding}</Pill> : u.mailbox ? 'None' : null}</Field>
              <Field label="Holds">{u.mailbox ? [u.mailbox.litigationHold && 'Litigation', u.mailbox.archive && 'Archive'].filter(Boolean).join(' · ') || 'None' : null}</Field>
              <Field label="Backup">
                {!u.backup ? <span className="text-muted-foreground">No SaaS Protection customer</span>
                  : u.backup.state === 'loading' ? <span className="text-muted-foreground">loading…</span>
                  : <Link to={backupSaasCustomerPath(u.backup.saasCustomerId)} className="hover:underline">
                      {u.backup.state === 'no-seat' ? <Pill tone="bad">no seat</Pill>
                        : <Pill tone={u.backup.seatState === 'Active' ? 'good' : u.backup.seatState === 'Unprotected' ? 'bad' : 'warn'}>{u.backup.seatState ?? 'seat'}</Pill>}
                      {u.backup.exchange && <span className="ml-1 text-xs text-muted-foreground">Exchange {u.backup.exchange}</span>}
                    </Link>}
              </Field>
            </div>
          </div>
          {u.tenantHealthy === false && <div className="border-t border-divider px-4 py-2 text-xs text-warning">CIPP can't currently reach this tenant; data may be old.</div>}
        </Panel>
      ))}
    </>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-4 text-xs text-muted-foreground">{children}</div>;
}
